import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSeoTitle } from "@/lib/useSeoTitle";

export default function ForgotPassword() {
  const nav = useNavigate();
  useSeoTitle({
    title: "Forgot password | TalesTalesTales",
    description: "Recover your TalesTalesTales account.",
    path: "/forgot",
    noindex: true,
  });
  const [step, setStep] = useState(1); // 1=email, 2=answer+new pw, 3=done
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first.");
    setBusy(true);
    try {
      const r = await api.post("/auth/security-question", { email });
      if (!r.data.question) {
        toast.error("No security question is set for this email — contact support.");
      } else {
        setQuestion(r.data.question);
        setStep(2);
      }
    } catch {
      toast.error("Could not look up your account.");
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (!answer || pw.length < 6) {
      return toast.error("Fill the answer and a new password (6+ chars).");
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset", {
        email, security_answer: answer, new_password: pw,
      });
      setStep(3);
      toast.success("Password reset. You can now sign in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="forgot-page" className="min-h-screen bg-[#0f0d11] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-amber-300/70 hover:text-amber-200 font-ui text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="parchment gold-border rounded-2xl p-8 candle-glow">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-5 h-5 text-amber-400 flicker" />
            <span className="font-ui text-[10px] uppercase tracking-[0.3em] text-amber-400/80">
              Recover your scroll
            </span>
          </div>
          <h1 className="font-display text-3xl text-amber-50 mb-4">Forgot password</h1>

          {step === 1 && (
            <form onSubmit={lookup} className="space-y-4">
              <p className="font-body text-amber-100/70 text-sm italic">
                Enter the email tied to your saga — we&apos;ll fetch the security question you set at registration.
              </p>
              <div>
                <Label className="font-ui text-amber-200">Email</Label>
                <Input
                  data-testid="forgot-email"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                />
              </div>
              <Button
                data-testid="forgot-lookup"
                type="submit" disabled={busy}
                className="w-full bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11"
              >
                <Flame className="w-4 h-4 mr-2" />
                {busy ? "Searching..." : "Find my question"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={reset} className="space-y-4">
              <div className="rounded-md border border-amber-700/30 bg-[#15121a] p-3">
                <div className="font-ui text-[10px] uppercase tracking-widest text-amber-400/70 mb-1">
                  Your security question
                </div>
                <p data-testid="forgot-question" className="font-body text-amber-100 italic">{question}</p>
              </div>
              <div>
                <Label className="font-ui text-amber-200">Answer</Label>
                <Input
                  data-testid="forgot-answer"
                  value={answer} onChange={(e) => setAnswer(e.target.value)}
                  className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                  placeholder="(case-insensitive)"
                />
              </div>
              <div>
                <Label className="font-ui text-amber-200">New password</Label>
                <Input
                  data-testid="forgot-new-pw"
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  minLength={6}
                  className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit" disabled={busy}
                className="w-full bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11"
              >
                {busy ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="font-body text-amber-100/85">
                Your password has been renewed. Light a candle and sign back in.
              </p>
              <Button
                data-testid="forgot-go-login"
                onClick={() => nav("/login")}
                className="w-full bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11"
              >
                <Flame className="w-4 h-4 mr-2" /> Return to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
      <section className="max-w-3xl mx-auto px-6 py-10 text-center">
        <h2 className="font-display text-2xl text-amber-100 mb-3">Recovering your TalesTalesTales account</h2>
        <p className="font-body text-amber-100/65 text-sm leading-relaxed">
          TalesTalesTales does not store your password — even we cannot read it. To keep your hero, party, custom worlds,
          and saved Dungeons &amp; Dragons campaigns safe, password recovery uses the security question you chose
          when you registered. Enter the email tied to your saga, answer your question (case-insensitive),
          and set a new password of at least 8 characters. If you never set a security question, please open a
          ticket on the <Link to="/support" className="gold-text hover:text-amber-200">Support page</Link> with the
          email you registered with — our keep-master will help recover your chronicle. Otherwise, you can always
          start fresh by <Link to="/register" className="gold-text hover:text-amber-200">forging a new hero</Link> on the free tier.
        </p>
      </section>
    </div>
  );
}
